import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { userLogoutAction } from "../../redux/actions/loginImport";
import { useCheckForValidToken } from "./useCheckForValidToken";

export const useTokenValidation = (userDataId) => {
  const dispatch = useDispatch();
  const { mutate: checkTokenValidation, data: isTokenValid, isLoading: isTokenValidLoading, isError: isTokenValidError } = useCheckForValidToken();
  const lastVisibilityCheck = useRef(0);

  // Interval-based token checking (every 2 hours)
  useEffect(() => {
    //console.log("Setting up token validation for userDataId:", userDataId);
    
    let interval;
    
    const checkToken = () => {
      //console.log("Checking token validation for userDataId:", userDataId);
      if (userDataId) {
        //console.log("User Id Present, can check for token expiration");
        checkTokenValidation({ token: userDataId });
      } else {
        //console.log("User Id not present, cannot check for token expiration");
      }
    };
    
    // Check immediately when component mounts or userDataId changes
    checkToken();
    
    // Set up interval to check every 2 hours
    if (userDataId) {
      interval = setInterval(checkToken, 2 * 60 * 60 * 1000); // 2 hours in milliseconds
      //console.log("Token validation interval set for every 2 hours");
    }
    
    // Cleanup function - clear interval when component unmounts or userDataId changes
    return () => {
      if (interval) {
        clearInterval(interval);
        //console.log("Token validation interval cleared");
      }
    };
  }, [userDataId, checkTokenValidation]);

  // Event-based token checking (when tab becomes visible)
  useEffect(() => {
    //console.log("Setting up visibility change listener for token validation");
    
    const handleVisibilityChange = () => {
      if (document.hidden || !userDataId) return;
    
      const now = Date.now();
      if (now - lastVisibilityCheck.current > 30 * 1000) {
        //console.log("Tab became visible, checking for token");
        lastVisibilityCheck.current = now;
        checkTokenValidation({ token: userDataId });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userDataId, checkTokenValidation]);

  // Handle token expiration - logout if token is invalid
  useEffect(() => {
    //console.log("Checking if token is valid:", isTokenValid);
    if (isTokenValidError) {
      console.error("Token validation error:", isTokenValidError);
      // Gt whats in the url after the .com/
      // If token is invalid, dispatch logout action (with that url)
      const currentUrl = window.location.href;
      const urlAfterDomain = currentUrl.split(window.location.origin)[1] || '/';
      //console.log("Redirecting to login due to token expiration, current URL:", urlAfterDomain);


      // Redirect to login if token is invalid
      dispatch(userLogoutAction({
        logoutType: 'token-expired',
        logoutRedirectPath: urlAfterDomain
      }));
      return;
    }
  }, [isTokenValidError, isTokenValid, dispatch]);

  // Return validation state (optional - you might not need these)
  return {
    isTokenValid,
    isTokenValidLoading,
    isTokenValidError
  };
};